/**
 * FB54 / FB65 — who is in this job's family, fetched once.
 *
 * Associated jobs are separate `jobs` rows sharing the `YY-NNNN` base (see
 * migration 0167 and `job-family.ts`). ONE query answers every family question
 * the page asks — which sibling sections the tab row needs (FB54), and which
 * member is the main job the URL has to be on (FB65) — so it is defined here as
 * shared query options rather than re-issued per consumer:
 *
 *   • `useJobFamily(jobNumber)` — the siblings, ordered for the tab row.
 *   • `familyQueryOptions(base)` — the same entry, awaited in the route's
 *     `beforeLoad` via `queryClient.ensureQueryData` so the redirect decision
 *     and the tab row are made from the SAME cached rows, one request in total.
 *
 * The query caches the WHOLE family under the base — every job in the family
 * shares that one entry — and "which of these is me" is applied afterwards, so
 * walking from 26-1273-WTR to 26-1273-MLD doesn't reuse a list that had the
 * wrong row filtered out.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '~/lib/supabase';
import { familyBaseOf, hasFamilyBase, siblingsOf, type FamilyMemberRef } from './job-family';
import { orderSiblings, type SiblingRef } from './top-tabs-model';

/** The cache key a family lives under. Keyed by BASE, never by job number. */
export function familyQueryKey(base: string): [string, string] {
  return ['job-family-tabs', base];
}

async function fetchFamily(base: string): Promise<FamilyMemberRef[]> {
  const { data, error } = await supabase
    .from('v_jobs_full')
    // `created_at` is what decides the main job (job-family.mainJobOf) — the
    // original intake owns the file, every later division is an addition to it.
    .select('job_number, division, created_at')
    .like('job_number', `${base}-%`)
    .order('job_number');
  if (error) throw error;
  return (data ?? [])
    .filter((r): r is { job_number: string; division: string | null; created_at: string | null } =>
      !!r.job_number)
    .map((r) => ({
      jobNumber: r.job_number,
      division: r.division ?? null,
      createdAt: r.created_at ?? null,
    }));
}

export function familyQueryOptions(base: string) {
  return {
    queryKey: familyQueryKey(base),
    queryFn: () => fetchFamily(base),
    staleTime: 60_000,
  };
}

/** Every member of `jobNumber`'s family, including the job itself. */
export function useJobFamilyMembers(jobNumber: string | null | undefined): FamilyMemberRef[] {
  const jn = (jobNumber ?? '').trim();
  const base = familyBaseOf(jn);
  const { data } = useQuery<FamilyMemberRef[]>({
    ...familyQueryOptions(base),
    // An off-format job number (no `-DIV` suffix to strip) has no family to
    // find; querying would `LIKE`-match unrelated rows.
    enabled: hasFamilyBase(jn),
  });
  return data ?? [];
}

/**
 * Siblings of `jobNumber`, ordered for the tab row. Never includes the job
 * being viewed; a solo job returns an empty list, which is what makes
 * `buildTopTabs` emit exactly the historic five tabs.
 */
export function useJobFamily(jobNumber: string | null | undefined): SiblingRef[] {
  const jn = (jobNumber ?? '').trim();
  const members = useJobFamilyMembers(jn);
  return useMemo(
    () => orderSiblings(
      siblingsOf(members, jn).map((m) => ({ jobNumber: m.jobNumber, division: m.division })),
    ),
    [members, jn],
  );
}
